import type { ReactNode } from "react";

interface SimpleMarkdownProps {
  markdown: string;
  className?: string;
}

type InlineToken =
  | { type: "text"; value: string }
  | { type: "strong"; value: string }
  | { type: "em"; value: string }
  | { type: "code"; value: string }
  | { type: "link"; value: string; href: string };

function pushTextToken(tokens: InlineToken[], value: string) {
  if (!value) return;
  tokens.push({ type: "text", value });
}

function parseInlineTokens(value: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*)/g;
  let lastIndex = 0;

  for (const match of value.matchAll(pattern)) {
    const full = match[0];
    const index = match.index ?? 0;
    pushTextToken(tokens, value.slice(lastIndex, index));

    if (match[2] && match[3]) {
      tokens.push({ type: "link", value: match[2], href: match[3] });
    } else if (match[4]) {
      tokens.push({ type: "strong", value: match[4] });
    } else if (match[5]) {
      tokens.push({ type: "code", value: match[5] });
    } else if (match[6]) {
      tokens.push({ type: "em", value: match[6] });
    } else {
      pushTextToken(tokens, full);
    }

    lastIndex = index + full.length;
  }

  pushTextToken(tokens, value.slice(lastIndex));
  return tokens;
}

function renderInlineMarkdown(value: string): ReactNode[] {
  return parseInlineTokens(value).map((token, index) => {
    const key = `${token.type}-${index}`;
    switch (token.type) {
      case "strong":
        return <strong key={key}>{token.value}</strong>;
      case "em":
        return <em key={key}>{token.value}</em>;
      case "code":
        return <code key={key}>{token.value}</code>;
      case "link":
        return (
          <a key={key} href={token.href} target="_blank" rel="noreferrer noopener">
            {token.value}
          </a>
        );
      default:
        return <span key={key}>{token.value}</span>;
    }
  });
}

function normalizeMarkdown(markdown: string): string[] {
  return markdown.replace(/\r\n/g, "\n").split("\n");
}

function renderBlocks(markdown: string): ReactNode[] {
  const lines = normalizeMarkdown(markdown);
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];

  function flushParagraph() {
    if (paragraph.length === 0) return;
    const text = paragraph.join(" ").trim();
    if (text) {
      blocks.push(<p key={`p-${blocks.length}`}>{renderInlineMarkdown(text)}</p>);
    }
    paragraph = [];
  }

  function flushList() {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`}>
        {listItems.map((item) => (
          <li key={`li-${item}`}>{renderInlineMarkdown(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  }

  function renderHeading(text: string, level: number, key: string) {
    if (level <= 4) return <h4 key={key}>{renderInlineMarkdown(text)}</h4>;
    if (level === 5) return <h5 key={key}>{renderInlineMarkdown(text)}</h5>;
    return <h6 key={key}>{renderInlineMarkdown(text)}</h6>;
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const headingMarks = headingMatch[1] ?? "";
      const text = headingMatch[2] ?? "";
      const level = Math.min(headingMarks.length + 3, 6);
      blocks.push(renderHeading(text, level, `h-${blocks.length}`));
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/) ?? line.match(/^\d+\.\s+(.+)$/);
    if (bulletMatch) {
      flushParagraph();
      const bulletText = bulletMatch[1];
      if (bulletText) {
        listItems.push(bulletText);
      }
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return blocks;
}

export function stripMarkdown(value: string): string {
  return value
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, "$1")
    .replace(/[*_`>#-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function SimpleMarkdown({ markdown, className }: SimpleMarkdownProps) {
  return <div className={className ?? "markdown-block"}>{renderBlocks(markdown)}</div>;
}
